import { style, globalStyle, keyframes } from '@vanilla-extract/css';
import { vars } from '../../theme/contract.css';
import { reducedMotion } from '../../theme/media';
import { glassRimLight, textOnGlassBase } from '../../styles/glass.css';

export const container = style({
  ...textOnGlassBase,
  display: 'flex',
  flexDirection: 'column',
  gap: vars.spacing.gap.sm,
  position: 'relative',
  background: `radial-gradient(ellipse at 50% 40%, ${vars.opacityScale.whiteOverlay.light} 0%, transparent 40%, ${vars.opacityScale.blackOverlay.fade} 100%), ${vars.surface.liquidGlass.innerSurface.background}`,
  backdropFilter: vars.surface.liquidGlass.backdropFilter.light,
  borderRadius: vars.spacing.borderRadius.md,
  padding: vars.spacing.padding.sm,
  border: `1px solid ${vars.surface.liquidGlass.innerSurface.border}`,
  boxShadow: `inset 0 0 20px ${vars.opacityScale.blackOverlay.medium}, inset 0 1px 0 ${vars.opacityScale.whiteOverlay.veil}, inset 0 -2px 6px ${vars.opacityScale.blackOverlay.strong}, 0 0 6px ${vars.bright.primary.glow}`,
  flex: 1,
  minHeight: 0,
});

globalStyle(`${container}::after`, glassRimLight);

export const barChart = style({
  display: 'flex',
  alignItems: 'flex-end',
  gap: vars.spacing.gap.xs,
  flex: 1,
  minHeight: '160px',
  paddingTop: vars.spacing.padding.sm,
});

export const barColumn = style({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: vars.spacing.gap['2xs'],
  height: '100%',
});

export const barColumnToday = style({
  position: 'relative',
  selectors: {
    '&::before': {
      content: '""',
      position: 'absolute',
      inset: '-4px -2px',
      borderRadius: vars.spacing.borderRadius.md,
      border: `1px solid ${vars.bright.secondary.default}`,
      boxShadow: `0 0 8px ${vars.opacityScale.cyan.strong}, inset 0 0 8px ${vars.opacityScale.cyan.dust}`,
      pointerEvents: 'none',
    },
  },
});

export const barTrack = style({
  flex: 1,
  width: '100%',
  position: 'relative',
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'center',
  borderRadius: `${vars.spacing.borderRadius.sm} ${vars.spacing.borderRadius.sm} 0 0`,
  overflow: 'hidden',
  background: vars.opacityScale.whiteOverlay.subtle,
  border: `1px solid ${vars.opacityScale.whiteOverlay.subtle}`,
  borderBottom: 'none',
});

export const barFill = style({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  borderRadius: `${vars.spacing.borderRadius.sm} ${vars.spacing.borderRadius.sm} 0 0`,
  transition: `height ${vars.animation.duration.slow} ${vars.animation.easing.easeOut}`,
  selectors: {
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: '10%',
      right: '10%',
      height: '1px',
      background: `linear-gradient(90deg, transparent, ${vars.opacityScale.whiteOverlay.heavy}, transparent)`,
      borderRadius: '1px',
    },
  },
  '@media': {
    [reducedMotion]: { transition: 'none' },
  },
});

export const barFillFull = style({
  background: `linear-gradient(to top, ${vars.bright.status.success}, ${vars.opacityScale.successGreen.medium})`,
  boxShadow: `0 0 8px ${vars.bright.status.success}, inset 0 1px 0 ${vars.opacityScale.whiteOverlay.notable}`,
});

export const barFillHalf = style({
  background: `linear-gradient(to top, ${vars.bright.status.info}, ${vars.surface.highlight.medium})`,
  boxShadow: `0 0 6px ${vars.bright.status.info}, inset 0 1px 0 ${vars.opacityScale.whiteOverlay.airy}`,
});

export const barFillLow = style({
  background: `linear-gradient(to top, ${vars.bright.status.warning}, ${vars.opacityScale.goldWarm.medium})`,
  boxShadow: `0 0 6px ${vars.bright.status.warning}, inset 0 1px 0 ${vars.opacityScale.whiteOverlay.airy}`,
});

const pulse = keyframes({
  '0%, 100%': {
    opacity: 0.75,
    boxShadow: `0 0 6px ${vars.bright.status.info}, 0 -1px 4px ${vars.bright.secondary.default}, inset 0 1px 0 ${vars.opacityScale.whiteOverlay.airy}`,
  },
  '50%': {
    opacity: 1,
    boxShadow: `0 0 14px ${vars.bright.status.info}, 0 -3px 12px ${vars.bright.secondary.default}, inset 0 1px 0 ${vars.opacityScale.whiteOverlay.intense}`,
  },
});

export const barFillActive = style({
  background: `linear-gradient(to top, ${vars.bright.status.info}, ${vars.surface.highlight.light})`,
  boxShadow: `0 0 10px ${vars.bright.status.info}, 0 -2px 8px ${vars.bright.secondary.default}, inset 0 1px 0 ${vars.opacityScale.whiteOverlay.strong}`,
  animation: `${pulse} ${vars.animation.duration.xslow} ease-in-out infinite`,
  '@media': {
    [reducedMotion]: { animation: 'none' },
  },
});

export const barFillOvertime = style({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  background: `linear-gradient(to top, transparent, ${vars.opacityScale.goldWarm.medium})`,
  boxShadow: `0 -2px 10px ${vars.opacityScale.goldWarm.strong}, 0 0 4px ${vars.bright.status.warning}`,
  borderRadius: `${vars.spacing.borderRadius.sm} ${vars.spacing.borderRadius.sm} 0 0`,
  pointerEvents: 'none',
});

export const barTargetLine = style({
  position: 'absolute',
  bottom: '100%',
  left: '-2px',
  right: '-2px',
  height: '2px',
  background: vars.opacityScale.whiteOverlay.strong,
  boxShadow: `0 0 6px ${vars.opacityScale.whiteOverlay.medium}, 0 0 2px ${vars.opacityScale.whiteOverlay.strong}`,
  pointerEvents: 'none',
  zIndex: 1,
});

export const barLeave = style({
  width: '100%',
  height: '24px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: vars.spacing.borderRadius.sm,
  background: vars.opacityScale.whiteOverlay.subtle,
  border: `1px dashed ${vars.opacityScale.whiteOverlay.medium}`,
});

export const barLeaveLabel = style({
  fontSize: vars.typography.fontSize.xs,
  color: vars.typography.textColor.tertiary,
  fontStyle: 'italic',
});

export const barEmpty = style({
  width: '100%',
  height: '4px',
  borderRadius: '2px',
  background: vars.opacityScale.whiteOverlay.subtle,
  alignSelf: 'flex-end',
});

export const barDuration = style({
  fontSize: vars.typography.fontSize.xs,
  fontFamily: vars.typography.fontFamily.mono,
  color: vars.typography.textColor.secondary,
  letterSpacing: vars.typography.letterSpacing.expanded,
  flexShrink: 0,
  lineHeight: 1,
});

export const barLabel = style({
  fontSize: vars.typography.fontSize.xs,
  fontFamily: vars.typography.fontFamily.mono,
  color: vars.typography.textColor.tertiary,
  letterSpacing: vars.typography.letterSpacing.expanded,
  flexShrink: 0,
});

export const barLabelToday = style({
  color: vars.bright.secondary.default,
  fontWeight: vars.typography.fontWeight.bold,
  textShadow: `0 0 6px ${vars.opacityScale.cyan.bold}`,
});

export const summary = style({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: vars.spacing.gap.sm,
  paddingTop: vars.spacing.padding.xs,
  borderTop: `1px solid ${vars.opacityScale.whiteOverlay.subtle}`,
  fontFamily: vars.typography.fontFamily.mono,
  fontSize: vars.typography.fontSize.sm,
});

export const summaryText = style({
  color: vars.typography.textColor.primary,
  fontWeight: vars.typography.fontWeight.medium,
});

export const summaryTarget = style({
  color: vars.typography.textColor.tertiary,
  fontWeight: vars.typography.fontWeight.regular,
});

export const summaryBar = style({
  display: 'flex',
  alignItems: 'center',
  gap: vars.spacing.gap.xs,
});

export const summaryBarTrack = style({
  width: '80px',
  height: '6px',
  borderRadius: '3px',
  background: vars.opacityScale.whiteOverlay.subtle,
  overflow: 'hidden',
  border: `1px solid ${vars.opacityScale.whiteOverlay.subtle}`,
});

export const summaryBarFill = style({
  display: 'block',
  height: '100%',
  borderRadius: '3px',
  transition: `width ${vars.animation.duration.fast} ${vars.animation.easing.easeOut}`,
  boxShadow: `0 0 6px ${vars.opacityScale.cyan.firm}`,
  '@media': {
    [reducedMotion]: { transition: 'none' },
  },
});

export const summaryPct = style({
  fontSize: vars.typography.fontSize.xs,
  color: vars.typography.textColor.tertiary,
  minWidth: '32px',
  textAlign: 'right',
});

export const legend = style({
  display: 'flex',
  alignItems: 'center',
  gap: vars.spacing.gap.sm,
  flexWrap: 'wrap',
  fontSize: vars.typography.fontSize.xs,
  fontFamily: vars.typography.fontFamily.mono,
  color: vars.typography.textColor.tertiary,
  letterSpacing: vars.typography.letterSpacing.expanded,
});

export const legendItem = style({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
});

export const legendDot = style({
  width: '8px',
  height: '8px',
  borderRadius: '2px',
  flexShrink: 0,
});

export const legendDotFull = style({
  background: vars.bright.status.success,
  boxShadow: `0 0 4px ${vars.bright.status.success}`,
});

export const legendDotHalf = style({
  background: vars.bright.status.info,
  boxShadow: `0 0 4px ${vars.bright.status.info}`,
});

export const legendDotLow = style({
  background: vars.bright.status.warning,
  boxShadow: `0 0 4px ${vars.bright.status.warning}`,
});

export const correctionMarker = style({
  width: 5,
  height: 5,
  borderRadius: '50%',
  backgroundColor: `${vars.bright.tailwind.amber500Strong}`,
  alignSelf: 'center',
  flexShrink: 0,
});

export const emptyState = style({
  padding: '24px',
  textAlign: 'center',
  color: vars.typography.textColor.tertiary,
  fontSize: vars.typography.fontSize.sm,
});
